import React, { useCallback, useEffect, useState } from "react";
import { Link, Redirect, Route, Switch, useLocation } from "react-router-dom";
import { Location } from "history";
// THIRD PARTIES
import * as Sentry from "@sentry/react";

// UTILITIES
import {
  fetchCurrentUser,
  fetchWithAuth,
  getAuthToken,
  hasAuthToken,
} from "../api-client/APIClient";
import { identifyPostHogUser } from "../posthog/identify";
import {
  INVOICE_FE_DISABLED_STATUSES,
  LinkedAccountMetrics,
  APICategory,
} from "../models/Entities";
import { getIntegrationCategoryList } from "../models/Helpers";

import {
  getIntegrationConfigurationPathForIndividualCategory,
  API_TESTER_PATH,
  API_TESTER_YOU_TO_MERGE_LINKED_ACCOUNT_PATH,
  BILLING_PATH,
  ADD_PAYMENT_METHOD_PATH,
  CONFIGURATION_INTEGRATIONS_PATH,
  CONFIGURATION_COMMON_MODELS_PATH,
  CONFIGURATION_PATH,
  DASHBOARD_PATH,
  ONBOARDING_PATH,
  ISSUES_PATH,
  LINKED_ACCOUNTS_PATH,
  LOGIN_PATH,
  LOGIN_WITH_SSO_PATH,
  LOGS_PATH,
  NOTIFICATIONS_SETTINGS_PATH,
  ORGANIZATION_PATH,
  ORGANIZATION_MEMBERS_PATH,
  ORGANIZATION_PENDING_PATH,
  AUDIT_TRAIL_PATH,
  PASSWORD_RESET_PATH,
  PASSWORD_SEND_RESET_PATH,
  PROFILE_PATH,
  SAML_NO_ACCOUNT_FOUND,
  SAML_RELAY_PATH,
  SECURITY_PATH_DEPRECATED,
  SIGNUP_PATH,
  API_KEYS_PATH,
  SETTINGS_PATHS,
  CONFIGURATION_INTEGRATIONS_ENDPOINT_USAGE_PATH,
} from "./RouterUtils";

import ErrorBoundary from "../components/shared/ErrorBoundary";
import AuthErrorFallback from "../components/pages/authentication/shared/AuthErrorFallback";

// CONTEXT
import { UserWithBillingPlan } from "../components/context/AppContext";
import AppContextProvider from "../components/context/AppContextProvider";

// WRAPPERS
import LeftNavigationBar from "../components/portal/LeftNavigationBar";

// AUTH
import SignUpScreen from "../components/pages/authentication/signup/SignUpScreen";
import Login from "../components/pages/authentication/login/Login";
import ResetPasswordScreen from "../components/pages/authentication/login/ResetPasswordScreen";
import SendResetPasswordScreen from "../components/pages/authentication/login/SendResetPasswordScreen";
import SendSsoUrlScreen from "../components/pages/authentication/login/SendSsoUrlScreen";
import SamlNoAccountFound from "../components/pages/authentication/saml/SamlNoAccountFound";
import SamlRedirectScreen from "../components/pages/authentication/saml/SamlRedirectScreen";

// ONBOARDING
import DashboardOnboarding from "../components/pages/get-started/DashboardOnboarding";

// DASHBOARD
import Dashboard from "../components/pages/dashboard/Dashboard";

// ADVANCED
import IndividualIssuePage from "../components/pages/issues/detail-page/IndividualIssuePage";
import IssuesPage from "../components/pages/issues/IssuesPage";
import LinkedAccountsTab from "../components/pages/linked-accounts/LinkedAccountsTab";
import ConfigurationIntegrationsPageWrapper from "../components/pages/configuration/integrations/ConfigurationIntegrationsPageWrapper";
import ConfigurationIntegrationsPage from "../components/pages/configuration/integrations/ConfigurationIntegrationsPage";
import ConfigurationCommonModelsFullPage from "../components/pages/configuration/common-models/ConfigurationCommonModelsFullPage";
import ConfigurationTab from "../components/pages/configuration/ConfigurationTab";

// DEVELOP
import APIKeysPage from "../components/pages/api-keys/APIKeysPage";

// ACCOUNT SETTINGS
import BillingPage from "../components/pages/settings/billing/BillingPage";
import AddPaymentMethod from "../components/pages/settings/billing/AddPaymentMethod";
import NotificationSettingsPage from "../components/pages/settings/notifications/NotificationSettingsPage";
import OrganizationPage from "../components/pages/settings/organization/OrganizationPage";
import Profile from "../components/pages/settings/profile/Profile";
import ErrorFallback from "../components/shared/ErrorFallback/ErrorFallback";

// COMPONENTS
import UpdatedTermsModal from "../components/portal/announcements/UpdatedTermsModal";
import ExploreMergeModal from "../components/portal/explore-merge-modal/ExploreMergeModal";
import TermsAcceptanceGate, {
  requiresTermsAcceptance,
} from "../components/portal/terms/TermsAcceptanceGate";

import { DashboardOnboardingStatus } from "../components/pages/get-started/types";
import { hasOrgOnboardedSuccessfully } from "../components/pages/get-started/utils/onboardingUtils";
import { BannerToShow } from "../components/shared/banners/DashboardBanners";
import { isRouteDisabled } from "../components/shared/utils/SharedComponentUtils";
import ComponentForFeature from "./ComponentForFeature";
import {
  isBillingPlanFreePlan,
  isBillingPlanLaunchPlan,
} from "../components/pages/settings/billing/BillingUtils";
import AuditLogsPage from "../components/pages/settings/audit-logs/AuditLogsPage";
import SettingsPage from "../components/pages/settings/SettingsPage";
import GatedRoute from "./GatedRoute";
import ApiTesterPage from "../components/pages/api-tester/ApiTesterPage";
import TagManager from "../react-gtm-module-nonce";
import { Menu as MenuIcon, X } from "lucide-react";
import { ReactComponent as Logo } from "../assets/svg/logos/merge/logo.svg";

import * as Frigade from "@frigade/react";

import { MergeFlagFeature, useMergeFlag } from "../components/shared/hooks/useMergeFlag";
import useStaticLinkedAccountCount from "../components/shared/hooks/useStaticLinkedAccountCount";
import useDashboardOnboardingStatus from "../components/pages/get-started/hooks/useDashboardOnboardingStatus";
import { FrigadeProvider, isFrigadeEnabled, npsFlowId } from "./FrigadeProvider";
import FrigadeAssistant from "./FrigadeAssistant";
import FrigadeFlows from "./FrigadeFlows";
import IntegrationEndpointUsagePage from "../components/pages/configuration/integrations/endpoint-usage/IntegrationEndpointUsagePage";
import { useFlow } from "@frigade/react";

const removeTrailingSlash = (pathOrUrl: string) =>
  pathOrUrl.endsWith("/") && pathOrUrl.length > 1
    ? pathOrUrl.substring(0, pathOrUrl.length - 1)
    : pathOrUrl;

const PYLON_APP_ID = "55deac61-3309-47d6-b720-05a1bb6d653b";

type PortalProps = {
  user: UserWithBillingPlan | undefined;
  setUser: (user: UserWithBillingPlan) => void;
  isExploreMergeOpen: boolean;
  onExploreMergeOpenChange: (isOpen: boolean) => void;
};

const PortalContainer = ({
  user,
  setUser,
  isExploreMergeOpen,
  onExploreMergeOpenChange,
}: PortalProps) => {
  const location = useLocation<Location>();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [fetchedOnboardingStatus, setFetchedOnboardingStatus] =
    useState<DashboardOnboardingStatus | null>(null);

  const [linkedAccountMetrics, setLinkedAccountMetrics] = useState<LinkedAccountMetrics>();
  const numberOfStaticLinkedAccounts = useStaticLinkedAccountCount();

  const { enabled: isEndpointUsageEnabled } = useMergeFlag({
    feature: MergeFlagFeature.MERGE_FLAG_ENABLE_ENDPOINT_USAGE,
    organizationId: user?.organization.id,
  });

  const { enabled: isPlaThresholdBannersEnabled } = useMergeFlag({
    feature: MergeFlagFeature.MERGE_FLAG_ENABLE_PLA_THRESHOLD_BANNERS,
    organizationId: user?.organization.id,
  });

  const shouldHomePageDefaultToDashboard = !!hasOrgOnboardedSuccessfully(
    fetchedOnboardingStatus ?? undefined,
  );

  const isLoadingOnboardingStatus = !fetchedOnboardingStatus;

  const integrationCategoryList = getIntegrationCategoryList(user);

  useEffect(() => {
    fetchWithAuth({
      path: "/users/me/onboarding-status",
      method: "GET",
      onResponse: (data: DashboardOnboardingStatus) => {
        setFetchedOnboardingStatus(data);
      },
    });
  }, []);

  useEffect(() => {
    fetchWithAuth({
      path: "/integrations/linked-accounts/detailed-counts",
      method: "GET",
      onResponse: (data: LinkedAccountMetrics) => {
        setLinkedAccountMetrics(data);
      },
    });
  }, []);

  const { addProperties: addFrigadeUserProperties } = Frigade.useUser();
  const { addProperties: addFrigadeGroupProperties } = Frigade.useGroup();

  useEffect(() => {
    if (user) {
      window.pylon = {
        chat_settings: {
          app_id: PYLON_APP_ID,
          email: user.email,
          name: user.name,
          avatar_url: user.avatar,
          email_hash: user.pylon_hmac,
        },
      };

      const numberofProductionLinkedAccounts =
        numberOfStaticLinkedAccounts ?? (user.first_prod_linked_account_created_at ? "N/A" : 0);

      if (isFrigadeEnabled(user)) {
        addFrigadeUserProperties({
          name: user.name,
          email: user.email,
          organization: user.organization.name,
          organizationId: user.organization.id,
          createdDate: user.terms_agreement_datetime,
          lastLoginDate: new Date(),
          role: user.type,
          numberOfLinkedAccounts: numberofProductionLinkedAccounts,
          hasFinishedOnboarding: shouldHomePageDefaultToDashboard,
          planTier:
            user.organization.organization_billing_plan?.billing_plan.plan_tier || "NO_PLAN_TIER",
          firstProdLinkedAccountCreatedAt: user.first_prod_linked_account_created_at ?? "N/A",
        });

        addFrigadeGroupProperties({
          id: user.organization.id,
          name: user.organization.name,
          planTier:
            user.organization.organization_billing_plan?.billing_plan.plan_tier || "NO_PLAN_TIER",
          production_linked_account_count: numberofProductionLinkedAccounts,
        });
      }
    }
  }, [user, numberOfStaticLinkedAccounts]);

  // Restart NPS flow if it's been more than 90 days since the last action
  const { flow } = useFlow(npsFlowId);
  useEffect(() => {
    if (flow?.isCompleted || flow?.isSkipped) {
      // Calculate last time since the flow was completed
      const completionDate = flow?.rawData?.$state?.lastActionAt;

      // If there's no completion date, no need to continue.
      if (!completionDate) return;

      // Convert the completion date into a Date object.
      // Get the current date and calculate the difference in days
      const lastActiveDate = new Date(completionDate);
      const now = new Date();
      const diffTime = now.getTime() - lastActiveDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      // If it's been more than 90 days since the last action, restart the flow.
      // This is a lossless operation as all previous data is saved.
      if (diffDays >= 90) {
        flow?.restart();
      }
    }
  }, [flow]);

  // Auto-close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const dashboardOnboardingStatus = useDashboardOnboardingStatus();

  if (!hasAuthToken()) {
    return <Redirect to={LOGIN_PATH} />;
  }

  if (!user) {
    return null;
  }

  /// /////////////////////////////
  /// /// Pricing Banner Methods & Constants  //////
  /// /////////////////////////////

  const isLaunchPlan = isBillingPlanLaunchPlan(
    user.organization.organization_billing_plan?.billing_plan,
  );

  const isFreePlan = isBillingPlanFreePlan(
    user.organization.organization_billing_plan?.billing_plan,
  );

  const showUpgradedBanner = user.show_upgrade_billing_plan_banner && isLaunchPlan;

  const numberOfCompletedAndIdleLinkedAccounts = linkedAccountMetrics
    ? linkedAccountMetrics.completed_linked_accounts_count +
      linkedAccountMetrics.idle_accounts_count +
      linkedAccountMetrics.rippling_oauth_accounts_count
    : undefined;
  const purchasedProdLinkedAccounts = user.organization.purchased_prod_linked_accounts;
  const dashboardDisabledForBilling =
    user.organization.invoice_overdue_status &&
    INVOICE_FE_DISABLED_STATUSES.includes(user.organization.invoice_overdue_status);

  // Show linkedAccountsApproachingLimitBanner if number of LAs is >= 90% total purchased LAs
  const linkedAccountsApproachingLimitBanner = isPlaThresholdBannersEnabled
    ? // NEW: plan-aware thresholds
      numberOfCompletedAndIdleLinkedAccounts !== undefined &&
      numberOfCompletedAndIdleLinkedAccounts > 0 &&
      (isFreePlan
        ? numberOfCompletedAndIdleLinkedAccounts < 3
        : !!purchasedProdLinkedAccounts &&
          numberOfCompletedAndIdleLinkedAccounts >= purchasedProdLinkedAccounts * 0.9 &&
          numberOfCompletedAndIdleLinkedAccounts < purchasedProdLinkedAccounts)
    : // OLD: hardcoded limit of 3
      numberOfStaticLinkedAccounts !== undefined &&
      numberOfStaticLinkedAccounts > 0 &&
      numberOfStaticLinkedAccounts <= 3;

  const linkedAccountsReachedLimitBanner = isPlaThresholdBannersEnabled
    ? // NEW: plan-aware thresholds
      numberOfCompletedAndIdleLinkedAccounts !== undefined &&
      (isFreePlan
        ? numberOfCompletedAndIdleLinkedAccounts >= 3
        : !!purchasedProdLinkedAccounts &&
          numberOfCompletedAndIdleLinkedAccounts >= purchasedProdLinkedAccounts)
    : // OLD: hardcoded above-3
      numberOfStaticLinkedAccounts !== undefined && numberOfStaticLinkedAccounts > 3;

  const bannerStatus =
    showUpgradedBanner ||
    user.organization.invoice_overdue_status ||
    user.organization.billing_guardrails_status ||
    null;

  const showAnyBanner =
    location.pathname !== ADD_PAYMENT_METHOD_PATH &&
    !user.is_pricing_banner_dismissed &&
    (linkedAccountsApproachingLimitBanner ||
      linkedAccountsReachedLimitBanner ||
      (bannerStatus !== null &&
        user.organization.average_production_linked_account_count !== undefined));

  // const isInvoiceDueDateNotNull = user.organization.invoice_due_date !== null;
  function formatInvoiceDueDate(dateString?: string): string {
    if (!dateString) {
      return "";
    }
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      month: "long",
      day: "numeric",
    };
    return `due ${date.toLocaleDateString("en-US", options)}`;
  }

  function isInvoiceDueDateNull(string: string | undefined) {
    return string === null;
  }

  function dismissPricingBanner() {
    fetchWithAuth({
      path: "/users/me",
      method: "PATCH",
      body: {
        is_pricing_banner_dismissed: true,
      },
      onResponse: (data) => {
        setUser(data.user);
      },
    });
  }

  return (
    <>
      <AppContextProvider user={user} setUser={setUser}>
        <div className="fixed inset-0 flex flex-col overflow-hidden">
          {/* Banners - natural height */}
          {location.pathname !== ADD_PAYMENT_METHOD_PATH && (
            <BannerToShow
              showAnyBanner={showAnyBanner}
              bannerStatus={bannerStatus}
              showUpgradedBanner={showUpgradedBanner}
              isFreePlan={isFreePlan}
              showApproachingLimitBanner={linkedAccountsApproachingLimitBanner}
              showLimitReachedBanner={linkedAccountsReachedLimitBanner}
              numberOfCompletedAndIdleLinkedAccounts={numberOfCompletedAndIdleLinkedAccounts}
              isInvoiceDueDateNull={isInvoiceDueDateNull(user.organization.invoice_due_date)}
              invoiceDueDate={formatInvoiceDueDate(user.organization.invoice_due_date)}
              dismiss={dismissPricingBanner}
              pricingBannerDismissed={user.is_pricing_banner_dismissed}
              isPlaThresholdBannersEnabled={isPlaThresholdBannersEnabled}
            />
          )}
          {!isExploreMergeOpen && <FrigadeFlows user={user} />}
          {/* Mobile header bar */}
          <div className="flex h-16 shrink-0 items-center border-b border-gray-10 bg-gray-0 px-4 md:hidden relative z-50">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              aria-label="Toggle navigation"
            >
              {isMobileMenuOpen ? <X size={24} /> : <MenuIcon size={24} />}
            </button>
            <Link to="/" className="ml-3">
              <Logo />
            </Link>
          </div>
          {/* Everything below banners - fills remaining space */}
          <div className="relative flex flex-1 overflow-hidden">
            <LeftNavigationBar
              dashboardOnboardingStatus={dashboardOnboardingStatus}
              mobileOpen={isMobileMenuOpen}
              onMobileClose={() => setIsMobileMenuOpen(false)}
            />
            <div className="main-content h-full flex-1 min-w-0 overflow-y-auto overscroll-y-none">
              <div className="w-full pl-6 pr-10">
                <Sentry.ErrorBoundary
                  onError={(error) => {
                    if (process.env.REACT_APP_MERGE_ENV !== "PRODUCTION") {
                      console.error(error);
                    }
                  }}
                  fallback={({ error }) => <ErrorFallback error={error} />}
                >
                  {!isLoadingOnboardingStatus && (
                    <Route
                      exact
                      path="/"
                      children={
                        shouldHomePageDefaultToDashboard ? (
                          <Dashboard />
                        ) : (
                          <DashboardOnboarding
                            dashboardOnboardingStatus={dashboardOnboardingStatus}
                          />
                        )
                      }
                    />
                  )}

                  <Route exact path={DASHBOARD_PATH} children={<Dashboard />} />

                  <Route
                    path={ONBOARDING_PATH}
                    children={
                      <DashboardOnboarding dashboardOnboardingStatus={dashboardOnboardingStatus} />
                    }
                  />

                  <GatedRoute path={LINKED_ACCOUNTS_PATH} children={<LinkedAccountsTab />} />

                  {!isRouteDisabled(user, LOGS_PATH) && !isRouteDisabled(user, ISSUES_PATH) && (
                    <Switch>
                      <Route
                        path={LOGS_PATH}
                        children={({ match }: { match: any }) => (
                          <ComponentForFeature feature="is_webhook_search_enabled" match={match} />
                        )}
                      />
                      <Route
                        path={`${ISSUES_PATH}/:integrationIssueID`}
                        children={<IndividualIssuePage />}
                      />
                    </Switch>
                  )}

                  {/* API issues */}
                  <GatedRoute exact path={ISSUES_PATH} children={<IssuesPage />} />

                  {/* API keys */}
                  <GatedRoute path={API_KEYS_PATH} children={<APIKeysPage />} />

                  {/* API tester */}
                  {/* Redirect from old API_TESTER_PATH  */}
                  <GatedRoute path={API_TESTER_PATH} exact>
                    <Redirect to={API_TESTER_YOU_TO_MERGE_LINKED_ACCOUNT_PATH} />
                  </GatedRoute>

                  <GatedRoute path={`${API_TESTER_PATH}/:direction/:linkedAccountType?`}>
                    <ApiTesterPage />
                  </GatedRoute>

                  {/* Integrations */}
                  <Route path={CONFIGURATION_INTEGRATIONS_PATH}>
                    <Switch>
                      {integrationCategoryList.map((category: APICategory) => (
                        <Route
                          key={category}
                          exact
                          path={getIntegrationConfigurationPathForIndividualCategory(category)}
                          render={() => (
                            <ConfigurationIntegrationsPage
                              category={category}
                              isEndpointUsageEnabled={!!isEndpointUsageEnabled}
                            />
                          )}
                        />
                      ))}
                      <Route
                        exact
                        path={CONFIGURATION_INTEGRATIONS_ENDPOINT_USAGE_PATH}
                        render={() => (
                          <IntegrationEndpointUsagePage
                            isEndpointUsageEnabled={isEndpointUsageEnabled}
                          />
                        )}
                      />
                      <Redirect
                        to={getIntegrationConfigurationPathForIndividualCategory(
                          integrationCategoryList[0],
                        )}
                      />
                    </Switch>
                  </Route>

                  {/* Scopes */}
                  <Route
                    path={CONFIGURATION_COMMON_MODELS_PATH}
                    children={<ConfigurationCommonModelsFullPage />}
                  />

                  {/* Advanced */}
                  <GatedRoute path={CONFIGURATION_PATH} children={<ConfigurationTab />} />
                  <GatedRoute
                    exact
                    path={ADD_PAYMENT_METHOD_PATH}
                    children={<AddPaymentMethod />}
                  />

                  {/* Settings/Profile Pages */}
                  <Route path={SETTINGS_PATHS}>
                    <SettingsPage>
                      <Switch>
                        <GatedRoute exact path={PROFILE_PATH} children={<Profile />} />
                        {/* Members is the default tab, so land on its path rather than the bare one */}
                        <Redirect exact from={ORGANIZATION_PATH} to={ORGANIZATION_MEMBERS_PATH} />
                        <GatedRoute
                          exact
                          path={ORGANIZATION_MEMBERS_PATH}
                          children={<OrganizationPage />}
                        />
                        <GatedRoute
                          exact
                          path={ORGANIZATION_PENDING_PATH}
                          children={<OrganizationPage />}
                        />
                        {/* anything else under /organization is not a tab, so send it to Members */}
                        <Redirect from={ORGANIZATION_PATH} to={ORGANIZATION_MEMBERS_PATH} />
                        <GatedRoute exact path={AUDIT_TRAIL_PATH} children={<AuditLogsPage />} />
                        <GatedRoute
                          path={NOTIFICATIONS_SETTINGS_PATH}
                          children={<NotificationSettingsPage />}
                        />
                        <GatedRoute exact path={BILLING_PATH} children={<BillingPage />} />

                        {/* the security page and path are deprecated but leaving this in to make sure links to the old path don't error */}
                        <Redirect from={SECURITY_PATH_DEPRECATED} to={PROFILE_PATH} />
                      </Switch>
                    </SettingsPage>
                  </Route>

                  {/* Reroute if the rest of dashboard is disabled */}
                  {dashboardDisabledForBilling ? <Redirect to={BILLING_PATH} /> : null}
                </Sentry.ErrorBoundary>
              </div>
            </div>
          </div>
          <UpdatedTermsModal />
          <ExploreMergeModal onOpenChange={onExploreMergeOpenChange} />
          {/* The Frigade Assistant mounts inside the authenticated portal only.
              The key forces a clean remount when the user logs out, switches
              organization, or otherwise changes identity — so the Assistant
              never carries stale identity into a new session. */}
          <FrigadeAssistant key={`${user.uuid}-${user.organization.id}`} />
        </div>
      </AppContextProvider>
    </>
  );
};

const AppRouter = () => {
  const [user, setUser] = useState<UserWithBillingPlan>();
  // Frigade's dialogs are modal Radix dialogs, which set pointer-events: none on <body> while open,
  // so none may open over the Explore Merge pick or the terms page.
  const [isExploreMergeOpen, setIsExploreMergeOpen] = useState(false);
  const location = useLocation<Location>();
  const authToken = getAuthToken();

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }

  const nonce = document.getElementById("csp-policy")?.getAttribute("nonce-id") || "";

  // @ts-ignore
  gtag("js", new Date());
  // @ts-ignore
  gtag("config", "UA-164577142-1");

  useEffect(() => {
    if (location.pathname === "/login" || location.pathname === "/signup") {
      TagManager.initialize({
        gtmId: "GTM-WZ3TTB4",
        nonce,
      });
    }
  }, [location]);

  // Don't attempt to load user on these pages.
  // Note that the Login page is excluded because the Login page will attempt to load user
  // and will redirect into the app on success, and will stay on login page on 403.
  const unauthenticatedPagePaths = [LOGIN_WITH_SSO_PATH, SAML_NO_ACCOUNT_FOUND, SAML_RELAY_PATH];

  const pathIsUnauthenticated = useCallback(
    (path?: string) => {
      if (!path) return false;
      const unauthPathsWithoutSlashes = unauthenticatedPagePaths.map(removeTrailingSlash);
      const currentPathWithoutSlash = removeTrailingSlash(path);
      return unauthPathsWithoutSlashes.includes(currentPathWithoutSlash);
    },
    [unauthenticatedPagePaths],
  );

  useEffect(() => {
    if (!user && !pathIsUnauthenticated(location.pathname)) {
      fetchCurrentUser(setUser);
    }
  }, [authToken, user, location.pathname, pathIsUnauthenticated]);

  useEffect(() => {
    if (user) {
      Sentry.setUser({ email: user.email, id: `${user.uuid}` });
    }
  }, [user]);

  // Identify the user to PostHog. PostHog only initializes when token + host
  // are present (see src/index.tsx); if it didn't init, posthog.identify is a
  // safe no-op. Reset on logout is wired directly into onLogout in
  // src/api-client/APIClient.tsx, not here, so we don't orphan the initial
  // $pageview by resetting before identify resolves on every page load.
  useEffect(() => {
    if (user) {
      identifyPostHogUser(user);
    }
  }, [user]);

  return (
    <FrigadeProvider
      user={user}
      defaultCollection={!isExploreMergeOpen && !requiresTermsAcceptance(user)}
    >
      <Switch>
        <Route
          exact
          path={SAML_RELAY_PATH}
          children={
            <ErrorBoundary boundary="saml-relay" fallback={<AuthErrorFallback />}>
              <SamlRedirectScreen />
            </ErrorBoundary>
          }
        />
        <Route
          exact
          path={PASSWORD_SEND_RESET_PATH}
          children={
            <ErrorBoundary boundary="password-send-reset" fallback={<AuthErrorFallback />}>
              <SendResetPasswordScreen />
            </ErrorBoundary>
          }
        />
        <Route
          path={PASSWORD_RESET_PATH}
          children={
            <ErrorBoundary boundary="password-reset" fallback={<AuthErrorFallback />}>
              <ResetPasswordScreen />
            </ErrorBoundary>
          }
        />
        <Route
          exact
          path={LOGIN_PATH}
          children={
            <ErrorBoundary boundary="login" fallback={<AuthErrorFallback />}>
              <Login setUser={setUser} />
            </ErrorBoundary>
          }
        />
        <Route
          exact
          path={SIGNUP_PATH}
          children={
            <ErrorBoundary boundary="signup" fallback={<AuthErrorFallback />}>
              <SignUpScreen setUser={setUser} />
            </ErrorBoundary>
          }
        />
        <Route
          exact
          path={LOGIN_WITH_SSO_PATH}
          children={
            <ErrorBoundary boundary="login-with-sso" fallback={<AuthErrorFallback />}>
              <SendSsoUrlScreen />
            </ErrorBoundary>
          }
        />
        <Route
          exact
          path={SAML_NO_ACCOUNT_FOUND}
          children={
            <ErrorBoundary boundary="saml-no-account-found" fallback={<AuthErrorFallback />}>
              <SamlNoAccountFound />
            </ErrorBoundary>
          }
        />
        <Route
          children={
            <TermsAcceptanceGate user={user} setUser={setUser}>
              <PortalContainer
                user={user}
                setUser={setUser}
                isExploreMergeOpen={isExploreMergeOpen}
                onExploreMergeOpenChange={setIsExploreMergeOpen}
              />
            </TermsAcceptanceGate>
          }
        />
      </Switch>
    </FrigadeProvider>
  );
};

export default AppRouter;
